import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Btn, Card, ConfirmModal, EmptyState, ErrorState, Loading, MetricCard,
  Overline, PageHeader, SearchBox, SegmentedControl, Toggle,
} from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import '../ForBrands/brandMode.css';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../constants';

const BASE = `${API_BASE_URL}/admin/email-campaigns/profile-completion`;

const PageShell = ({ children }) => (
  <div className="w-full min-w-0 max-w-full">{children}</div>
);

const Header = () => (
  <PageHeader
    eyebrow="Admin Mode"
    title="Emails"
    subtitle="Profile-completion drip for athletes who registered but have not finished setup. Up to 7 reminders per athlete, sent 3 days apart until the profile is 100% complete. Each reminder goes out as soon as it is due, and the cooldown keeps the same athlete from getting back-to-back emails. You can also send manually from here."
  />
);

// The bm positive colour (tailwind.config.js bm.positive) for Badge dots.
const POSITIVE = '#1f9d55';

// One Badge per drip state: brand when an email is due, positive when the
// profile is complete, attention in the cooldown, passive otherwise.
export const StatusPill = ({ candidate }) => {
  if (candidate.eligible) {
    return <Badge tone="brand">Email {candidate.due_milestone} due</Badge>;
  }
  if (candidate.reason_code === 'profile_complete') {
    return <Badge tone="outline" dot={POSITIVE}>Complete</Badge>;
  }
  if (candidate.reason_code === 'too_recent') {
    return <Badge tone="passive">Just registered</Badge>;
  }
  if (candidate.reason_code === 'all_milestones_sent') {
    return <Badge tone="passive">All sent</Badge>;
  }
  if (candidate.reason_code === 'between_milestones') {
    return <Badge tone="passive">Waiting</Badge>;
  }
  if (candidate.reason_code === 'cooldown') {
    const next = candidate.next_eligible_at ? formatDate(candidate.next_eligible_at) : null;
    return <Badge tone="attention">{next ? `Cooldown until ${next}` : 'Cooldown (7d)'}</Badge>;
  }
  return <Badge tone="passive">{candidate.reason_code || 'Idle'}</Badge>;
};

const TH = 'whitespace-nowrap border-b border-bm-border px-4 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
const TD = 'px-4 py-2.5 bm-body-m text-bm-ink';

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const EmailCampaignsPage = () => {
  const token = useSelector((s) => s.auth.token);
  const user = token ? JSON.parse(atob(token.split('.')[1])) : {};
  const isAdmin = (user.permissions || []).includes('IS_ADMIN');

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmSweep, setConfirmSweep] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sweeping, setSweeping] = useState(false);
  const [sendingFor, setSendingFor] = useState(null);
  const [autoSweepEnabled, setAutoSweepEnabled] = useState(false);
  const [autoSweepUpdatedAt, setAutoSweepUpdatedAt] = useState(null);
  const [autoSweepUpdatedBy, setAutoSweepUpdatedBy] = useState('');
  const [togglePending, setTogglePending] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BASE}/dashboard`, { headers: authHeaders });
      setDashboard(res.data);
      const s = res.data?.settings || {};
      setAutoSweepEnabled(!!s.auto_sweep_enabled);
      setAutoSweepUpdatedAt(s.updated_at || null);
      setAutoSweepUpdatedBy(s.updated_by || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard.');
    }
    setLoading(false);
  };

  const handleAutoSweepToggle = async () => {
    if (togglePending) return;
    const nextEnabled = !autoSweepEnabled;
    setTogglePending(true);
    setError('');
    try {
      const res = await axios.post(
        `${BASE}/settings`,
        { auto_sweep_enabled: nextEnabled, run_now: nextEnabled },
        { headers: authHeaders },
      );
      const s = res.data?.settings || {};
      setAutoSweepEnabled(!!s.auto_sweep_enabled);
      setAutoSweepUpdatedAt(s.updated_at || null);
      setAutoSweepUpdatedBy(s.updated_by || '');
      if (nextEnabled) {
        const sw = res.data?.sweep || {};
        toast.success(
          `Auto-sweep on. Sent ${sw.sent ?? 0} reminder${sw.sent === 1 ? '' : 's'} now; the daily cron will continue.`,
        );
      } else {
        toast.success('Auto-sweep paused. No more reminders will send until you turn it back on.');
      }
      loadDashboard();
    } catch (err) {
      setError(err.response?.data?.error || 'Toggle failed.');
    }
    setTogglePending(false);
  };

  useEffect(() => {
    if (isAdmin) loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Runs once the admin confirms in the ConfirmModal.
  const runSweepNow = async () => {
    setConfirmSweep(false);
    if (sweeping) return;
    setSweeping(true);
    setError('');
    try {
      const res = await axios.post(`${BASE}/run-sweep`, {}, { headers: authHeaders });
      const { sent, failed, skipped, total_scanned } = res.data || {};
      toast.success(`Sweep complete. Sent ${sent}, failed ${failed}, skipped ${skipped}, scanned ${total_scanned}.`);
      loadDashboard();
    } catch (err) {
      setError(err.response?.data?.error || 'Sweep failed.');
    }
    setSweeping(false);
  };

  const sendOne = async (candidate, force = false) => {
    if (sendingFor) return;
    setSendingFor(candidate.email);
    setError('');
    try {
      const payload = { email: candidate.email };
      if (candidate.due_milestone) payload.milestone = candidate.due_milestone;
      if (force) payload.force = true;
      await axios.post(`${BASE}/send`, payload, { headers: authHeaders });
      toast.success(`Sent email ${candidate.due_milestone || ''} to ${candidate.email}.`);
      loadDashboard();
    } catch (err) {
      setError(err.response?.data?.error || 'Send failed.');
    }
    setSendingFor(null);
  };

  // Test-mode resend. The backend records the log row as status='test'
  // so it doesn't count toward MAX_EMAILS or block the next due send.
  // Lets the admin QA the email copy at any milestone without disturbing
  // the athlete's drip sequence.
  const resendOne = async (candidate) => {
    if (sendingFor) return;
    const milestone = candidate.due_milestone || candidate.last_sent_milestone || 1;
    setSendingFor(`resend:${candidate.email}`);
    setError('');
    try {
      await axios.post(
        `${BASE}/send`,
        { email: candidate.email, milestone, test: true },
        { headers: authHeaders },
      );
      toast.success(`Test email ${milestone} sent to ${candidate.email} (doesn't count toward the 7-email cap).`);
    } catch (err) {
      setError(err.response?.data?.error || 'Test send failed.');
    }
    setSendingFor(null);
  };

  const filteredCandidates = useMemo(() => {
    const list = dashboard?.candidates || [];
    const term = search.trim().toLowerCase();
    return list.filter((c) => {
      if (filter === 'eligible' && !c.eligible) return false;
      if (filter === 'sent_any' && (!c.sent_milestones || c.sent_milestones.length === 0)) return false;
      if (filter === 'complete' && c.reason_code !== 'profile_complete') return false;
      if (!term) return true;
      const hay = `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase();
      return hay.includes(term);
    });
  }, [dashboard, search, filter]);

  if (!isAdmin) {
    return (
      <PageShell>
        <Header />
        <Card className="bm-body-m text-bm-muted">Admin access required.</Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header />

      {error && (
        <div className="mb-4">
          <ErrorState>{error}</ErrorState>
        </div>
      )}

      {/* Auto-sweep toggle. When ON, the daily cron sends due reminders and
          flipping it to ON catches up immediately. When OFF, the cron skips
          sending entirely; manual per-row sends and the 'Run sweep now'
          button still work, because they are explicit consent. */}
      <Card padding="p-5" className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="bm-label-l text-bm-ink">Automatic sweep</span>
            {autoSweepEnabled
              ? <Badge tone="outline" dot={POSITIVE}>On</Badge>
              : <Badge tone="passive">Off</Badge>}
          </div>
          <div className="bm-body-s text-bm-muted">
            {autoSweepEnabled
              ? 'Each athlete gets their next due reminder automatically as soon as it\'s due, up to 7 total, 3 days apart.'
              : 'No automatic emails will send. Manual per-row sends and "Run sweep now" still work.'}
          </div>
          {autoSweepUpdatedAt && (
            <div className="bm-body-s text-bm-tertiary">
              Last changed {formatDate(autoSweepUpdatedAt)}
              {autoSweepUpdatedBy ? ` by ${autoSweepUpdatedBy}` : ''}.
            </div>
          )}
        </div>
        <span className={togglePending ? 'cursor-wait opacity-60' : ''}>
          <Toggle on={autoSweepEnabled} onChange={handleAutoSweepToggle} label="Automatic sweep" />
        </span>
      </Card>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <MetricCard label="Candidates" value={dashboard?.totals?.candidates ?? '-'} />
        <MetricCard label="Eligible now" value={dashboard?.totals?.eligible_now ?? '-'} />
        <MetricCard label="Sent (all time)" value={dashboard?.totals?.sent_total ?? '-'} />
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Overline>View</Overline>
          {/* Four options run wider than a phone: keep each label on one
              line and scroll inside the row instead. */}
          <div className="max-w-full overflow-x-auto whitespace-nowrap">
            <SegmentedControl
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All athletes' },
                { value: 'eligible', label: 'Eligible now' },
                { value: 'sent_any', label: 'Already emailed' },
                { value: 'complete', label: 'Complete' },
              ]}
              capitalize={false}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search athlete by name or email..."
            aria-label="Search athletes"
            className="w-full sm:w-72"
          />
          <Btn onClick={() => setConfirmSweep(true)} disabled={sweeping}>
            <Icon name="refresh" size={14} className={`mr-1.5 ${sweeping ? 'animate-spin' : ''}`} />
            {sweeping ? 'Sweeping...' : 'Run sweep now'}
          </Btn>
        </div>
      </div>

      {/* A plain yes or no: the sweep only sends reminders that are due, so
          there is nothing to acknowledge. */}
      {confirmSweep && (
        <ConfirmModal
          title="Run the sweep now?"
          body="This sends every due reminder immediately, whether or not the automatic sweep is on."
          confirmLabel="Run sweep now"
          cancelLabel="Cancel"
          requireAck={false}
          onConfirm={runSweepNow}
          onClose={() => setConfirmSweep(false)}
        />
      )}

      <div className="overflow-x-auto rounded-[12px] border border-bm-border bg-white">
        <table className="w-full min-w-[860px] text-left">
          <thead className="bg-bm-subtle">
            <tr>
              {['Athlete', 'Registered', 'Missing', 'Status', 'Sent', 'Last sent'].map((h) => (
                <th key={h} className={TH}>{h}</th>
              ))}
              <th className={`${TH} !text-center`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><Loading label="Loading athletes..." /></td></tr>
            ) : filteredCandidates.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4">
                  <EmptyState>No athletes match this view.</EmptyState>
                </td>
              </tr>
            ) : filteredCandidates.map((c) => {
              const sending = sendingFor === c.email;
              return (
                <tr
                  key={c.email}
                  className="border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle"
                >
                  <td className={TD}>
                    <div className="flex items-center gap-2">
                      <Icon name="user" size={14} className="text-bm-tertiary" />
                      <div>
                        <div className="bm-label-l text-bm-ink">
                          {(c.first_name || c.last_name) ? `${c.first_name} ${c.last_name}`.trim() : '-'}
                        </div>
                        <div className="bm-body-s text-bm-muted">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap text-bm-muted`}>
                    {formatDate(c.registered_at) || '-'}
                    {typeof c.days_since_registration === 'number' && (
                      <div className="mt-0.5 flex items-center gap-1 bm-body-s text-bm-tertiary">
                        <Icon name="clock" size={11} />
                        {c.days_since_registration} day{c.days_since_registration === 1 ? '' : 's'} ago
                      </div>
                    )}
                  </td>
                  <td className={`${TD} text-bm-muted`}>
                    {c.missing_summary
                      ? <span className="bm-body-s">{c.missing_summary}</span>
                      : <span className="text-bm-tertiary">-</span>}
                    {typeof c.completion_pct === 'number' && (
                      <div className="mt-1 flex items-center gap-1.5">
                        <div className="h-1 max-w-[70px] flex-1 overflow-hidden rounded-full bg-bm-mutedbg">
                          <div
                            className="h-full rounded-full bg-bm-orange"
                            style={{ width: `${Math.max(c.completion_pct, 4)}%` }}
                          />
                        </div>
                        <span className="bm-body-s tabular-nums text-bm-muted">
                          {c.completion_pct}%
                        </span>
                      </div>
                    )}
                  </td>
                  <td className={TD}><StatusPill candidate={c} /></td>
                  <td className={`${TD} text-bm-muted`}>
                    {(c.sent_milestones || []).length === 0
                      ? <span className="text-bm-tertiary">-</span>
                      : <Badge tone="outline" dot={POSITIVE}>{c.sent_milestones.length} of 7</Badge>}
                  </td>
                  <td className={`${TD} whitespace-nowrap text-bm-muted`}>
                    {c.last_sent_at
                      ? (
                        <div>
                          <div className="bm-body-s">{formatDate(c.last_sent_at)}</div>
                          <div className="bm-body-s text-bm-tertiary">Email {c.last_sent_milestone}</div>
                        </div>
                      )
                      : <span className="text-bm-tertiary">-</span>}
                  </td>
                  <td className={TD}>
                    <div className="flex items-center justify-center gap-1.5">
                      <Btn
                        size="sm"
                        onClick={() => sendOne(c)}
                        disabled={!c.eligible || sending}
                        title={c.eligible
                          ? `Send email ${c.due_milestone} of 7`
                          : 'Not eligible right now'}
                      >
                        <Icon name="mail" size={12} className="mr-1" />
                        {sending && sendingFor === c.email ? 'Sending' : c.eligible ? `Send #${c.due_milestone}` : 'Send'}
                      </Btn>
                      <Btn
                        size="sm"
                        variant="secondary"
                        onClick={() => resendOne(c)}
                        disabled={sending}
                        title="Test send (doesn't count toward the 7-email cap or affect cooldown)"
                      >
                        <Icon name="refresh" size={12} className="mr-1" />
                        {sendingFor === `resend:${c.email}` ? '...' : 'Resend'}
                      </Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
};

export default EmailCampaignsPage;
